import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Hospital, CheckCircle2, Users, Shield, Zap } from 'lucide-react';
import { healthcareServiceSchema, breadcrumbSchema } from '../utils/structuredData';

const HealthcarePage = () => {
  const features = [
    'Virtual walkthroughs of neonatal units and clinical spaces',
    'Advance familiarization for parents and families',
    'Wayfinding and navigation support',
    'Multi-language accessibility options',
    'Equipment and environment explanations',
    'Secure, privacy-focused delivery'
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Reduce Anxiety',
      description: 'Help families feel more prepared by removing fear of the unknown'
    },
    {
      icon: Users,
      title: 'Improve Understanding',
      description: 'Clear visual information about environments and processes'
    },
    {
      icon: Zap,
      title: 'Support Inclusivity',
      description: 'Multi-language support and accessible information for all'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Healthcare 3D Virtual Tours | NHS Patient Journey Support"
        description="3D virtual tours for NHS healthcare facilities. Help patients and families prepare for neonatal units and reduce anxiety. Contact us today."
        keywords="healthcare 3D tours, neonatal unit virtual tour, NHS patient experience, hospital wayfinding, patient anxiety reduction, healthcare technology, virtual healthcare environments, CQC patient care"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/healthcare` : ''}
        structuredData={[
          healthcareServiceSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Healthcare', url: typeof window !== 'undefined' ? `${window.location.origin}/healthcare` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-50 rounded-full mb-6">
              <Hospital className="h-6 w-6 text-green-600" />
              <span className="text-green-600 font-semibold">HEALTHCARE SERVICES</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Making Clinical Environments More Familiar
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Supporting parents and patients through healthcare journeys with 3D virtual technology that reduces anxiety and improves preparedness.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1766299892690-9a52631d1b68?w=1400&q=85"
              alt="Mother holding newborn in hospital - supporting families through the patient journey"
              className="w-full h-[500px] object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Supporting Parents Through the Neonatal Journey
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  For parents, a neonatal unit can feel overwhelming — unfamiliar machines, clinical environments and uncertainty at an already emotional time.
                </p>
                <p>
                  We use 3D scanning and immersive virtual technology to help parents become familiar with neonatal units before their baby is admitted, reducing anxiety and helping families feel more prepared and supported.
                </p>
                <p className="font-semibold text-gray-900 mt-6 italic">
                  "Being able to see the unit beforehand made a real difference for us. It helped take away some of the fear of the unknown and meant we felt more prepared when the time came."
                </p>
                <p className="text-sm text-green-600">— Parent</p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Healthcare Scanning Services</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <button className="mt-8 w-full px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-green-500/50 transition-all duration-300">
                Request a Quote
              </button>
            </div>
          </div>

          {/* Interactive 3D Model Example */}
          <div className="mt-20">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Interactive 3D Model Example</h2>
              <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
                Explore our 3D scan of an NHS Neonatal Unit directly below
              </p>
              <div className="bg-white rounded-2xl p-4 shadow-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  NHS Neonatal Unit
                </h3>
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src="https://digitalscanninggroup.com/3d-model/nhs-neonatal-unit/skinned/"
                    className="absolute top-0 left-0 w-full h-full rounded-xl border-2 border-gray-200"
                    title="NHS Neonatal Unit - 3D Model"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <p className="text-gray-600 mt-4 text-center">
                  Use your mouse to navigate • Scroll to zoom • Click and drag to rotate
                </p>
              </div>
            </div>
          </div>

          {/* Additional Healthcare Information */}
          <div className="mt-20 space-y-12">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Extending Support Across NHS Services</h3>
              <p className="text-gray-600 mb-4">
                While particularly valuable in neonatal and paediatric settings, this approach applies across many NHS areas where patients attend unfamiliar clinical environments.
              </p>
              <p className="text-gray-600">
                Digital environments support maternity units, cancer screening centres, outpatient clinics, diagnostic departments and specialist treatment areas.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white border-2 border-green-100 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Supporting Wayfinding</h3>
                <p className="text-gray-600 mb-4">
                  Virtual environments include clear wayfinding tools, helping patients navigate complex hospital sites.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2"><span className="text-green-600">✓</span> Visual routes through departments</li>
                  <li className="flex items-start gap-2"><span className="text-green-600">✓</span> Key location identification</li>
                  <li className="flex items-start gap-2"><span className="text-green-600">✓</span> Clear signposting to treatment areas</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-green-100 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Inclusive & Accessible Care</h3>
                <p className="text-gray-600 mb-4">
                  Multi-language options ensure information is accessible to diverse communities.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2"><span className="text-green-600">✓</span> Improved understanding</li>
                  <li className="flex items-start gap-2"><span className="text-green-600">✓</span> More inclusive experiences</li>
                  <li className="flex items-start gap-2"><span className="text-green-600">✓</span> Clearer communication</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">NHS & CQC Alignment</h3>
              <p className="text-gray-600 mb-6">
                Our approach aligns with Care Quality Commission priorities and NHS patient experience frameworks:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Responsive</h4>
                  <p className="text-gray-600 text-sm">Supporting patients to prepare and reducing anxiety</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Caring</h4>
                  <p className="text-gray-600 text-sm">Patient-centred approach acknowledging emotional needs</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Well-Led</h4>
                  <p className="text-gray-600 text-sm">Proactive service design and information sharing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HealthcarePage;
